import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface RaremarqSectionProps {
  activeFilter?: string | null;
}

export default function RaremarqSection({ activeFilter }: RaremarqSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const allProjects = [
    {
      id: 'raremarq-brand',
      title: 'Raremarq Rebrand Visual Identity',
      description:
        'Designing the full brand identity for a VC-backed fintech startup backed by LAUNCH and Jason Calacanis (early investor in Uber, Thumbtack, and Calm).',
      tags: ['Branding', 'Visual Design', 'Startup'],
      filterTags: ['UI/UX', 'Branding', 'Graphic Design'],
      imageUrl:
        'https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/bc9fa62c8c6b9250c7291d37da082115.png',
      link: '/raremarq-brand-identity',
      imageStyle: 'contain' as const,
      imageScale: 1,
      comingSoon: false,
    },
    {
      id: 'raremarq-web',
      title: "Raremarq's Web Experience",
      description:
        'Leading UX design for the web platform, blending finance and design to create intuitive, data-driven experiences.',
      tags: ['Product Design', 'UX Strategy', 'Web App', 'Marketplace'],
      filterTags: ['UI/UX', 'Branding', 'Graphic Design'],
      imageUrl:
        'https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/300912c791a3be8d2518669ca638dba6.png',
      link: '/raremarq-web-platform',
      imageStyle: 'contain' as const,
      imageScale: 1.55,
      comingSoon: false,
    },
    {
      id: 'humanmarq-ai',
      title: 'HumanMarq AI',
      description:
        'AI-powered authentication and verification system for collectibles, leveraging machine learning to ensure provenance and trust in every transaction.',
      tags: ['AI/ML', 'Authentication', 'Product Feature'],
      filterTags: ['UI/UX', 'AI/ML'],
      imageUrl: 'https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/09b2e855b8366559d31cbd26dbde2adf.png',
      link: '/humanmarq-ai',
      imageStyle: 'cover' as const,
      imageScale: 1,
      comingSoon: false,
    },
    {
      id: 'cgc-integration',
      title: 'CGC Integration',
      description:
        'Seamless integration with CGC grading services, enabling real-time grade verification and certified collectible management within the platform.',
      tags: ['Integration', 'Grading', 'API Design'],
      filterTags: ['API Design', 'UI/UX'],
      imageUrl: 'https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/319e851a2f7fc4abae1e1dac6e18d995.png',
      link: '/cgc-integration',
      imageStyle: 'cover' as const,
      imageScale: 1,
      comingSoon: false,
    },
  ];

  // Filter projects based on active filter
  const projects = activeFilter
    ? allProjects.filter((p) => p.filterTags.includes(activeFilter))
    : allProjects;

  return (
    <motion.div
      className="border border-notion-border rounded-lg bg-notion-sidebar overflow-hidden"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Raremarq Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 p-5 pb-4 hover:bg-notion-hover/50 transition-colors cursor-pointer text-left"
      >
        <span
          className="w-5 h-5 flex items-center justify-center text-notion-secondary transition-transform duration-200"
          style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
        >
          <i className="ri-arrow-right-s-line text-base"></i>
        </span>
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-7 h-7 rounded-md overflow-hidden flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/103400bb7f2f5a5b21e4eda213a3f655.png"
              alt="Raremarq Logo"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="font-serif text-[17px] font-medium text-notion-text leading-tight">
              Raremarq
            </h3>
            <p className="text-[11px] text-notion-secondary mt-0.5">
              VC-backed startup &middot; Backed by LAUNCH, Jason Calacanis &amp; Hyphen Capital
            </p>
          </div>
        </div>
        <span className="text-xs text-notion-secondary bg-notion-tag px-2 py-0.5 rounded whitespace-nowrap">
          2024–Present
        </span>
      </button>

      {/* Expanded Content */}
      <AnimatePresence initial={false}>
        {isExpanded && projects.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5">
              {/* All Projects - 2 column grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {projects.map((project, index) => {
                  const isHovered = hoveredId === project.id;
                  const hoverScale = project.comingSoon ? project.imageScale : project.imageScale * 1.05;
                  
                  return (
                    <motion.a
                      key={project.id}
                      href={project.comingSoon ? undefined : project.link}
                      data-project-card
                      data-coming-soon={project.comingSoon ? 'true' : undefined}
                      className={`block group ${project.comingSoon ? 'cursor-default' : 'cursor-pointer'}`}
                      onMouseEnter={() => setHoveredId(project.id)}
                      onMouseLeave={() => setHoveredId(null)}
                      onClick={(e) => {
                        if (project.comingSoon) {
                          e.preventDefault();
                        }
                      }}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.4 }}
                    >
                      <div className={`relative overflow-hidden rounded-lg border border-notion-border bg-white h-full transition-shadow duration-300 ${isHovered && !project.comingSoon ? 'shadow-md' : ''}`}>
                        {/* Media Container */}
                        <div className="relative w-full h-[200px] overflow-hidden bg-[#f5f0eb]">
                          <img
                            src={project.imageUrl}
                            alt={project.title}
                            title={`${project.title} - Annie Ding Portfolio`}
                            className={`absolute inset-0 w-full h-full object-center transition-transform duration-500 ${
                              project.imageStyle === 'contain' ? 'object-contain p-3' : 'object-cover'
                            }`}
                            style={{ 
                              transform: `scale(${isHovered && !project.comingSoon ? hoverScale : project.imageScale})` 
                            }}
                          />
                        </div>

                        {/* Content */}
                        <div className="p-5">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className={`text-[15px] font-medium text-notion-text transition-colors ${!project.comingSoon && 'group-hover:text-notion-accent'}`}>
                              {project.title}
                            </h3>
                          </div>
                          <p className="text-[13px] text-notion-secondary leading-relaxed mb-3">
                            {project.description}
                          </p>
                          {/* Tags */}
                          <div className="flex flex-wrap gap-1.5">
                            {project.tags.map((tag) => (
                              <span
                                key={tag}
                                className="text-[11px] text-notion-secondary bg-notion-tag px-2 py-0.5 rounded"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.a>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
